import React from "react";
import PropTypes from "prop-types";
import "./FunnelChart.scss";

const f = (n) => (n == null ? "—" : Number(n).toLocaleString("en-GB"));
const p = (n, d) => {
  if (!d) return "—";
  const v = (n / d) * 100;
  return v > 0 && v < 1 ? `${v.toFixed(1)}%` : `${Math.round(v)}%`;
};

/**
 * FunnelChart — a journey as tapering bars, widest first.
 *
 * Each step shows its count and its share of the START; between steps, how many carried
 * on. The step that loses the most people is marked, because that is the one to work on.
 * `previous` (same keys) adds a faint ghost of last period's shape behind each bar.
 */
const FunnelChart = ({ steps, color, previous, onStepClick, activeKey, unit }) => {
  const first = steps[0]?.value || 0;
  let worst = -1;
  let worstLoss = -1;
  steps.forEach((s, i) => {
    if (i === 0 || !steps[i - 1].value) return;
    const loss = 1 - s.value / steps[i - 1].value;
    if (loss > worstLoss) { worstLoss = loss; worst = i; }
  });
  const prevFirst = previous ? previous[steps[0]?.key] || 0 : 0;

  return (
    <div className="funnel" style={color ? { "--funnel-color": color } : undefined}>
      {steps.map((s, i) => {
        const w = first ? Math.max(s.value / first, s.value ? 0.012 : 0) : 0;
        const pw = previous && prevFirst ? (previous[s.key] || 0) / prevFirst : null;
        const prevStep = i > 0 ? steps[i - 1] : null;
        const Tag = onStepClick ? "button" : "div";
        return (
          <React.Fragment key={s.key}>
            {prevStep && (
              <div className={`funnel__link${i === worst ? " funnel__link--worst" : ""}`}>
                <span>
                  {p(s.value, prevStep.value)} carried on
                  {i === worst && <strong> · biggest drop</strong>}
                </span>
              </div>
            )}
            <Tag
              type={onStepClick ? "button" : undefined}
              className={`funnel__step${activeKey === s.key ? " funnel__step--active" : ""}${onStepClick ? " funnel__step--clickable" : ""}`}
              onClick={onStepClick ? () => onStepClick(s) : undefined}
              title={s.help || undefined}
            >
              <span className="funnel__label">{s.label}</span>
              <span className="funnel__track">
                {pw != null && <span className="funnel__ghost" style={{ width: `${Math.min(pw, 1) * 100}%` }} />}
                <span className="funnel__bar" style={{ width: `${w * 100}%`, opacity: 1 - i * (0.5 / steps.length) }} />
              </span>
              <span className="funnel__figures">
                <strong>{f(s.value)}</strong>
                <span>{i === 0 ? unit || "" : `${p(s.value, first)} of start`}</span>
              </span>
            </Tag>
          </React.Fragment>
        );
      })}
      {previous && <div className="funnel__legend"><span className="funnel__legend-ghost" /> previous period’s shape</div>}
    </div>
  );
};

FunnelChart.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    value: PropTypes.number.isRequired,
    help: PropTypes.string,
  })).isRequired,
  color: PropTypes.string,
  previous: PropTypes.object,
  onStepClick: PropTypes.func,
  activeKey: PropTypes.string,
  unit: PropTypes.string,
};

export default FunnelChart;
